import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'mizan.theme';
const DEFAULT = 'dark';

function readStored() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

function apply(theme) {
  document.documentElement.dataset.theme = theme;
}

function persist(theme) {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore — private mode etc. */
  }
}

/**
 * useTheme — hook returns the active theme and a toggle/set pair.
 * The initial value is whatever the anti-flash script in
 * public/index.html already set on <html data-theme="…">, falling
 * back to the localStorage value, falling back to 'dark'.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(() => {
    if (typeof document !== 'undefined' && document.documentElement.dataset.theme) {
      return document.documentElement.dataset.theme;
    }
    return readStored();
  });

  useEffect(() => {
    apply(theme);
    persist(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next === 'dark' || next === 'light') {
      setThemeState(next);
    }
  }, []);

  const toggle = useCallback(() => {
    setThemeState((curr) => (curr === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, setTheme, toggle };
}

export const THEME_STORAGE_KEY = STORAGE_KEY;
export const DEFAULT_THEME = DEFAULT;
